import React, { useState, useEffect, useMemo } from 'react';
import Fader from 'react-fader';
import { Route, useHistory } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { AxiosResponse } from 'axios';

import RegistrationOnboardingLayout from 'components/RegistrationOnboardingLayout';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useIsInsideApp from 'hooks/useIsInsideApp';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Response } from 'models/response';
import { RestCountryResponse } from 'models/restCountry';
import { VerifyInviteExistsResponse } from 'services/loginService';
import * as otpService from 'services/otpService';
import { VerifyLinkedRappiUserParameters, VerifyLinkedRappiUserResponse } from 'services/registrationService';
import { extractErrorMessage } from 'utils/generic';
import { goBackToApp } from 'utils/webViewUtils';

import ConfirmEmail, { ConfirmEmailValues } from './forms/ConfirmEmail';
import ConfirmEmailExists from './forms/ConfirmEmail/Exists';
import ConfirmEmailNotFound from './forms/ConfirmEmail/NotFound';
import Help from './forms/Help';
import HelpMail from './forms/Help/HelpMail';
import JoinRappi from './forms/JoinRappi';
import Telephone, { OTPChannel, TelephoneValues } from './forms/Telephone';
import TwoFactor, { TwoFactorCodeValues, TwoFactorData } from './forms/TwoFactor';
import VerificationCode, { VerificationCodeValues } from './forms/VerificationCode';

interface Props {
  fetchCountryCodes: () => Promise<void>;
  restCountries: RestCountryResponse[];
  loadingRestCountries: boolean;
  verifyInviteExists: (email: string) => Promise<Response<VerifyInviteExistsResponse>>;
  verifyLinkedUser: (data: VerifyLinkedRappiUserParameters) => Promise<Response<VerifyLinkedRappiUserResponse>>;
  resendInvite: (email: string) => Promise<AxiosResponse<void>>;
  resendTwoFactor: (key: string) => Promise<AxiosResponse<void>>;
}

const LANDING_URL = ENV.LANDING_PAGE as string;

const Login = ({
  fetchCountryCodes,
  restCountries,
  loadingRestCountries,
  verifyInviteExists,
  verifyLinkedUser,
  resendInvite,
  resendTwoFactor,
}: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const history = useHistory();
  const isMobile = useIsMobile();
  const isInsideApp = useIsInsideApp();

  const [telephone, setTelephone] = useState<TelephoneValues | null>(null);
  const [otpChannel, setOtpChannel] = useState<OTPChannel>();
  const [confirmEmail, setConfirmEmail] = useState<ConfirmEmailValues | null>(null);
  const [verificationCode, setVerificationCode] = useState<VerificationCodeValues | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState<TwoFactorCodeValues | null>(null);
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorData | null>(null);
  const [otpSendResponse, setOtpSendResponse] = useState<
    otpService.CreateWhatsAppOTPResponse | otpService.CreateSMSOTPResponse | null
  >(null);

  const defaultErrorHandler = (error: any) =>
    toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });

  useEffect(() => {
    fetchCountryCodes().catch(defaultErrorHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryCodes: CountryCode[] = useMemo(
    () =>
      restCountries
        .filter((c) => c.callingCodes.length > 0 && c.callingCodes[0])
        .map((c) => ({
          id: c.alpha2Code,
          name: c.name,
          flag: c.flag,
          code: `+${c.callingCodes[0]}`,
        }))
        .sort((a, b) => (Number(a.code.replace(/[\D]/g, '')) > Number(b.code.replace(/[\D]/g, '')) ? 1 : -1)),
    [restCountries],
  );

  const handleTelephoneSubmit = async (values: TelephoneValues, channel: OTPChannel, redirect = true) => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Telephone submit', data: { channel, values } });

    if (!channel) return;

    try {
      if (channel === 'whatsapp') {
        setOtpSendResponse(
          await otpService.sendWhatsAppOTP({
            country_code: values.country_code.code,
            phone: values.phone,
          }),
        );
      } else if (channel === 'sms') {
        setOtpSendResponse(
          await otpService.sendSMSOTP({
            country_code: values.country_code.code,
            phone: values.phone,
            locale: t('locale').substring(0, 2),
            via: 'sms',
          }),
        );
      }

      if (redirect) history.push('/login/verification-code');
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      if (errorCode) {
        toast.add({ type: 'danger', message: t([`login.error.${errorCode}` || e.response.data.error.message]) });
      } else {
        defaultErrorHandler(e);
      }
    } finally {
      setTelephone(values);
      setOtpChannel(channel);
    }
  };
  const handleConfirmEmailSubmit = async (values: ConfirmEmailValues) => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Confirm email submit', data: values });

    setConfirmEmail(values);
    const inviteResponse = await verifyInviteExists(values.email);
    if (inviteResponse.data.exists) {
      resendInvite(values.email);
      history.push('/login/confirm-email/exists');
      return;
    }

    const linkedResponse = await verifyLinkedUser(values);
    if (linkedResponse.data === 'NoUser') {
      history.push('/login/confirm-email/not-found');
      return;
    }

    history.push('/login/confirm-email/incorrect-phone');
  };
  const handleConfirmEmailExistsSubmit = () => {
    window.location.href = LANDING_URL;
  };
  const handleConfirmEmailExistsResend = async () => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Confirm email resend' });

    if (!confirmEmail) return;
    try {
      await resendInvite(confirmEmail.email);
      toast.add({ type: 'success', message: t('login.confirmEmail.exists.resendResult.success') });
    } catch (e) {
      toast.add({ type: 'success', message: t('login.confirmEmail.exists.resendResult.error') });
    }
  };
  const handleConfirmEmailNotFoundHelp = () => history.push('/login/confirm-email/help');
  const handleResendCode = () => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Resend code' });

    if (!telephone || !otpChannel) return;
    return handleTelephoneSubmit(telephone, otpChannel, false);
  };
  const handleVerificationCodeSubmit = async (values: VerificationCodeValues) => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Verification code submit', data: { otpChannel, values } });

    if (!telephone || !otpChannel || !otpSendResponse) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      let otpValidateResponse;
      if (otpChannel === 'whatsapp') {
        otpValidateResponse = await otpService.validateWhatsAppOTP({
          country_code,
          phone,
          code: values.code,
        });
      } else if (otpChannel === 'sms') {
        otpValidateResponse = await otpService.validateSMSOTP({
          country_code,
          phone,
          code: values.code,
          uuid: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
        });
      }

      Sentry.addBreadcrumb({ category: 'login', message: 'OTP validate response', data: otpValidateResponse });

      if (!otpValidateResponse.success && !otpValidateResponse.valid) {
        Sentry.addBreadcrumb({ category: 'login', message: 'OTP validate response fail', data: otpValidateResponse });
        Sentry.captureMessage('[Login] OTP validate response fail');
        toast.add({ type: 'danger', message: t('login.error.invalid_code') });
        return;
      }

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP({
          accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
          email: '',
          first_name: '',
          last_name: '',
          name: '',
          gender: '',
          replacement_criteria_id: 'replace',
          scope: 'all',
          social_id: phone,
          global_phone_number: `${country_code}${phone}`,
          sms_code: '',
          validate_code: false,
          phone,
          country_code,
        });
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP({
          accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
          country_code,
          phone,
          scope: 'all',
          social_id: phone,
        });
      }

      Sentry.addBreadcrumb({ category: 'login', message: 'OTP authenticate response', data: otpAuthenticateResponse });

      history.push(`/?token=${otpAuthenticateResponse.access_token}`);
    } catch (e) {
      const status = e.response?.status;
      const errors = e.response?.data?.errors;
      const error = e.response?.data?.error;

      if (status === 400) {
        if (errors?.name || errors?.nombre || errors?.email) {
          Sentry.addBreadcrumb({ category: 'login', message: "OTP responded that the Rappi user doesn't exist" });
          history.push('/login/join-rappi');
        } else if (error.code === 'verification_code_required') {
          Sentry.addBreadcrumb({ category: 'login', message: '2FA required for this user', data: error });
          setTwoFactorData(error);
          history.push('/login/two-factor');
        } else {
          defaultErrorHandler(e);
        }
      } else {
        defaultErrorHandler(e);
      }

      Sentry.withScope(function (scope) {
        scope.setTag('country_code', country_code ?? '');
        scope.setTag('phone', phone ?? '');
        scope.setTag('channel', otpChannel ?? '');

        Sentry.captureMessage('[Login] Error on verification code submit');
      });
    } finally {
      setVerificationCode(values);
    }
  };
  const handleResendTwoFactorCode = async () => {
    if (!twoFactorData) return;
    try {
      await resendTwoFactor(twoFactorData.key);
    } catch (e) {
      const errorCode = e.response?.data?.error?.code;

      Sentry.addBreadcrumb({
        category: 'login',
        message: '2FA code resend failed',
        data: { code: errorCode, response: e.response },
      });

      if (errorCode) {
        toast.add({ type: 'danger', message: t([`login.error.${errorCode}` || e.response.data.error.message]) });
        history.replace('/login');
      } else {
        defaultErrorHandler(e);
      }

      Sentry.captureMessage('[Login] Failed to resend 2FA code');
    }
  };
  const handleTwoFactorCodeSubmit = async (values: TwoFactorCodeValues) => {
    Sentry.addBreadcrumb({ category: 'login', message: 'Two factor code submit', data: values });

    if (!telephone || !twoFactorData || !otpSendResponse || !otpChannel) return;

    const phone = telephone.phone;
    const country_code = telephone.country_code.code;

    try {
      const otpAuth = {
        'x-two-factor-auth': twoFactorData.key,
        'x-two-factor-code': values.code,
      };

      let otpAuthenticateResponse;
      if (otpChannel === 'whatsapp') {
        otpAuthenticateResponse = await otpService.authenticateWhatsAppOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateWhatsAppOTPResponse).uuid,
            email: '',
            first_name: '',
            last_name: '',
            name: '',
            gender: '',
            replacement_criteria_id: 'replace',
            scope: 'all',
            social_id: phone,
            global_phone_number: `${country_code}${phone}`,
            sms_code: '',
            validate_code: false,
            phone,
            country_code,
          },
          otpAuth,
        );
      } else if (otpChannel === 'sms') {
        otpAuthenticateResponse = await otpService.authenticateSMSOTP(
          {
            accessToken: (otpSendResponse as otpService.CreateSMSOTPResponse).sid,
            country_code,
            phone,
            scope: 'all',
            social_id: phone,
          },
          otpAuth,
        );
      }

      Sentry.addBreadcrumb({
        category: 'login',
        message: 'Two factor OTP authenticate response',
        data: otpAuthenticateResponse,
      });

      history.push(`/?token=${otpAuthenticateResponse.access_token}`);
    } catch (e) {
      const error = e.response?.data?.error;
      const status = e.response?.status;

      if (status === 400 && error?.code === 'invalid_code') {
        Sentry.captureMessage('[Login] 2FA code is invalid');
        toast.add({ type: 'danger', message: t('login.error.invalid_code') });
        return;
      }

      defaultErrorHandler(e);
      Sentry.captureMessage('[Login] Error on 2FA verification code submit');
    } finally {
      setTwoFactorCode(values);
    }
  };

  const current = history.location.pathname.split('/').last();
  const getAllowBack = () => {
    if (current === 'login') return isInsideApp;
    return true;
  };
  const goBack = () => {
    if (current === 'login') goBackToApp();
    else history.goBack();
  };
  const getDefaultCountry = () => {
    const { hostname } = window.location;

    if (hostname.endsWith('uy'))
      return { id: 'UY', name: 'Uruguai', culture: 'es-UY', currency: 'UYU', documentType: '' };
    if (hostname.endsWith('ar'))
      return { id: 'AR', name: 'Argentina', culture: 'es-AR', currency: 'ARS', documentType: '' };
    if (hostname.endsWith('br'))
      return { id: 'BR', name: 'Brasil', culture: 'pt-BR', currency: 'BRL', documentType: '' };
    if (hostname.endsWith('mx'))
      return { id: 'MX', name: 'Mexico', culture: 'es-MX', currency: 'MXN', documentType: '' };

    return { id: 'CO', name: 'Colombia', culture: 'es-CO', currency: 'COP', documentType: '' };
  };

  const loginOAuth = () => {
    window.location.href = `${ENV.RAPPI_OAUTH.LOGIN_URL}?client_id=${ENV.RAPPI_OAUTH.LOGIN_CLIENTID}&redirect_uri=${
      ENV.RAPPI_OAUTH.REDIRECT_URL_FROM_LOGIN
    }&response_type=code&scope=all&flowName=GeneralOAuthFlow&state=${Math.random()}&subdomain=travel-corporate`;
  };

  return (
    <RegistrationOnboardingLayout
      appBarTitle={t('login.mobileTitle')}
      current={current}
      noGoBack={!getAllowBack()}
      onGoBack={goBack}
    >
      <Switch
        render={({ children }: any) => (
          <Fader style={isMobile ? { height: '100%' } : {}} viewStyle={isMobile ? { height: '100%' } : {}}>
            {children}
          </Fader>
        )}
      >
        <Route
          exact
          path="/login"
          component={() => (
            <Telephone
              values={telephone}
              onSubmit={handleTelephoneSubmit}
              countryCodes={countryCodes}
              country={getDefaultCountry()}
              loadingCountries={loadingRestCountries}
              loginOAuth={loginOAuth}
            />
          )}
        />
        <Route
          exact
          path="/login/confirm-email"
          component={() => <ConfirmEmail values={confirmEmail} onSubmit={handleConfirmEmailSubmit} />}
        />
        <Route
          exact
          confirmEmail={confirmEmail}
          path="/login/confirm-email/exists"
          component={() => (
            <ConfirmEmailExists
              confirmEmail={confirmEmail}
              onSubmit={handleConfirmEmailExistsSubmit}
              onResend={handleConfirmEmailExistsResend}
            />
          )}
        />
        <Route
          exact
          confirmEmail={confirmEmail}
          path="/login/confirm-email/not-found"
          component={() => <ConfirmEmailNotFound confirmEmail={confirmEmail} onHelp={handleConfirmEmailNotFoundHelp} />}
        />
        <Route
          exact
          path="/login/confirm-email/incorrect-phone"
          component={() => (
            <Telephone
              variant="incorrect-number"
              values={telephone}
              onSubmit={handleTelephoneSubmit}
              countryCodes={countryCodes}
              country={getDefaultCountry()}
              loadingCountries={loadingRestCountries}
              loginOAuth={loginOAuth}
            />
          )}
        />
        <Route exact path="/login/confirm-email/help" component={() => <Help />} />
        <Route
          exact
          path="/login/confirm-email/help/email"
          component={() => <HelpMail confirmEmail={confirmEmail} />}
        />
        <Route exact path="/login/join-rappi" component={() => <JoinRappi telephone={telephone} />} />
        <Route
          exact
          path="/login/verification-code"
          component={() => (
            <VerificationCode
              values={verificationCode}
              onSubmit={handleVerificationCodeSubmit}
              onResend={handleResendCode}
              telephone={telephone}
              channel={otpChannel}
            />
          )}
        />
        <Route
          exact
          path="/login/two-factor"
          component={() => (
            <TwoFactor
              values={twoFactorCode}
              onSubmit={handleTwoFactorCodeSubmit}
              onResend={handleResendTwoFactorCode}
              data={twoFactorData}
              telephone={telephone}
            />
          )}
        />
      </Switch>
    </RegistrationOnboardingLayout>
  );
};

export default Login;
